import type { ChatToolCall } from '../api/domains/chatbot.api'

/**
 * Assistant tools that change a script (notebook) on the server. When a finished turn carries a
 * successful one, the /scripts pages re-read: `apps/console` has no query cache to invalidate, so
 * this module-level signal stands in for it (the same shape as `forms-refresh.ts`).
 */
export const SCRIPTS_WRITE_TOOLS: ReadonlySet<string> = new Set([
  'create_notebook',
  'rename_notebook',
  'delete_notebook',
  'write_notebook_code',
  'run_notebook',
  'set_schedule',
  'pause_schedule',
  'resume_schedule',
  'clear_schedule',
  'set_notebook_access',
])

export interface ScriptsChange {
  /** Notebooks that changed or were created. */
  notebookIds: string[]
  /**
   * Notebooks that were run (`run_notebook`). A run changes the run history, not the notebook, so a
   * page re-reads only its history: re-reading the notebook would re-seed the settings panel and
   * wipe edits the user has not saved.
   */
  ranNotebookIds: string[]
  /** Notebooks that no longer exist. */
  deletedNotebookIds: string[]
}

type Listener = (change: ScriptsChange) => void

const listeners = new Set<Listener>()

/** Subscribes to assistant writes; returns the unsubscribe function. */
export function onScriptsWrite(listener: Listener): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

interface WriteResult {
  success?: unknown
  notebook_id?: unknown
}

const idOf = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)

/** A result counts only when `success === true` and it names its notebook; refusals change nothing. */
export function announceScriptsWrite(toolCalls: readonly ChatToolCall[]): void {
  const notebookIds: string[] = []
  const ranNotebookIds: string[] = []
  const deletedNotebookIds: string[] = []

  for (const call of toolCalls) {
    if (!SCRIPTS_WRITE_TOOLS.has(call.name)) continue
    const result = call.result as WriteResult | null

    if (!result || typeof result !== 'object' || result.success !== true) continue

    const id = idOf(result.notebook_id)
    if (!id) continue

    if (call.name === 'delete_notebook') deletedNotebookIds.push(id)
    else if (call.name === 'run_notebook') ranNotebookIds.push(id)
    else notebookIds.push(id)
  }

  if (notebookIds.length === 0 && ranNotebookIds.length === 0 && deletedNotebookIds.length === 0) return

  for (const listener of [...listeners]) {
    try {
      listener({ notebookIds, ranNotebookIds, deletedNotebookIds })
    } catch {
      // One page's failed refresh must not stop the others.
    }
  }
}

/** True when a change names this notebook, changed, run or deleted. */
export function changeTouchesNotebook(change: ScriptsChange, notebookId: string | null | undefined): boolean {
  if (!notebookId) return false

  return (
    change.notebookIds.includes(notebookId) ||
    change.ranNotebookIds.includes(notebookId) ||
    change.deletedNotebookIds.includes(notebookId)
  )
}
